import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadFileSmart } from "../lib/upload";
import { AppHeader } from "../components/AppHeader";
import { FilesList } from "../components/SecureFile";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, Upload, Calendar, X } from "lucide-react";
import { ATTENDANCE_META } from "../lib/attendance";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function nextWednesday() {
  const d = new Date();
  const diff = (3 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export default function Rattrapages() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    class_id: "",
    subject_id: "",
    selected: {},
    description: "",
    scheduled_date: nextWednesday(),
    files: [],
  });
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const loadMine = useCallback(async () => {
    const { data } = await api.get("/rattrapages/mine");
    setMine(data);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/classes"), api.get("/subjects")]).then(([c, s]) => {
      setClasses(c.data);
      setSubjects(s.data);
      setForm((f) => ({ ...f, class_id: c.data[0]?.id || "", subject_id: s.data[0]?.id || "" }));
    });
    loadMine();
  }, [loadMine]);

  useEffect(() => {
    if (!form.class_id) return;
    api.get(`/classes/${form.class_id}/students`)
      .then(({ data }) => setStudents(data))
      .catch(() => setStudents([]));
    setForm((f) => ({ ...f, selected: {} }));
  }, [form.class_id]);

  const toggleStudent = (id) => {
    setForm((f) => ({ ...f, selected: { ...f.selected, [id]: !f.selected[id] } }));
  };

  const handleFiles = async (fileList) => {
    for (const file of Array.from(fileList)) {
      setUploading({ name: file.name, progress: 0 });
      try {
        const uploaded = await uploadFileSmart(file, (p) => setUploading({ name: file.name, progress: p }));
        setForm((f) => ({ ...f, files: [...f.files, uploaded] }));
        toast.success(`${file.name} téléversé`);
      } catch (e) {
        toast.error(`Échec du téléversement de ${file.name} : ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
      }
    }
    setUploading(null);
  };

  const removeFile = (path) => {
    setForm((f) => ({ ...f, files: f.files.filter((x) => x.path !== path) }));
  };

  const submit = async () => {
    const student_ids = Object.keys(form.selected).filter((k) => form.selected[k]);
    if (!student_ids.length) {
      toast.error("Cochez au moins un élève");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Décrivez le devoir à rattraper");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/rattrapages", {
        class_id: form.class_id,
        subject_id: form.subject_id,
        student_ids,
        description: form.description,
        scheduled_date: form.scheduled_date,
        files: form.files,
      });
      toast.success(`Rattrapage créé pour ${student_ids.length} élève${student_ids.length > 1 ? "s" : ""}`);
      setForm((f) => ({ ...f, selected: {}, description: "", files: [] }));
      await loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/rattrapages/${id}`);
      setConfirmId(null);
      toast.success("Rattrapage supprimé");
      await loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const selectedCount = Object.values(form.selected).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="ratt-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 pb-8 border-b border-[var(--parchment-2)]">
          <div className="eyebrow">Rattrapages · Étude du mercredi</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Créer un rattrapage</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Sélectionnez une classe et une matière, cochez les élèves concernés, décrivez le travail à
            rattraper et joignez vos documents (images, PDF, vidéos — jusqu'à 50 Mo).
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Creation form */}
          <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="rattrapage-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-1">Classe</label>
                <select className={inputCls} value={form.class_id} onChange={(e) => setForm((f) => ({ ...f, class_id: e.target.value }))} data-testid="ratt-class-select">
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1">Matière</label>
                <select className={inputCls} value={form.subject_id} onChange={(e) => setForm((f) => ({ ...f, subject_id: e.target.value }))} data-testid="ratt-subject-select">
                  {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Élèves concernés ({selectedCount} sélectionné{selectedCount > 1 ? "s" : ""})</label>
              <div className="max-h-56 overflow-y-auto border border-[var(--parchment-2)] divide-y divide-[var(--parchment-2)]" data-testid="ratt-students-list">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--parchment)]/50">
                    <input
                      type="checkbox"
                      checked={!!form.selected[s.id]}
                      onChange={() => toggleStudent(s.id)}
                      className="accent-[var(--maroon)] w-4 h-4"
                      data-testid={`ratt-student-check-${s.id}`}
                    />
                    <span>{s.last_name.toUpperCase()} {s.first_name}</span>
                  </label>
                ))}
                {students.length === 0 && <div className="px-3 py-3 text-sm text-[var(--ink-soft)]">Aucun élève.</div>}
              </div>
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Date d'étude prévue (mercredi)</label>
              <input type="date" className={inputCls} value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} data-testid="ratt-date-input" />
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Description du travail</label>
              <textarea
                rows={3}
                className={inputCls}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Ex. Refaire les exercices 3, 5 et 7 p.42 du manuel"
                data-testid="ratt-description-input"
              />
            </div>

            <div className="mt-4">
              <label className="eyebrow block mb-1">Documents joints (images, PDF, vidéos)</label>
              <label className="btn-secondary !py-2 !px-3 cursor-pointer inline-flex items-center gap-2 text-sm">
                <Upload size={14} />
                {uploading ? `${uploading.name} · ${uploading.progress}%` : "Ajouter des documents"}
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf,video/*"
                  onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                  className="hidden"
                  data-testid="ratt-files-input"
                />
              </label>
              {form.files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="ratt-attached-files">
                  {form.files.map((f) => (
                    <span key={f.path} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[var(--parchment)] border border-[var(--parchment-2)]">
                      {f.name}
                      <button type="button" onClick={() => removeFile(f.path)} className="text-[var(--muted)] hover:text-[var(--late)]">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <button className="btn-primary mt-6 w-full justify-center" onClick={submit} disabled={submitting || !!uploading} data-testid="ratt-submit-btn">
              <Plus size={16} /> {submitting ? "Création..." : "Créer le rattrapage"}
            </button>
          </div>

          {/* My rattrapages */}
          <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="my-rattrapages">
            <div className="pb-3 border-b border-[var(--parchment-2)]">
              <div className="eyebrow">Mes rattrapages</div>
              <h2 className="font-serif-display text-2xl font-semibold mt-1">{mine.length} rattrapage{mine.length > 1 ? "s" : ""}</h2>
            </div>
            <div className="mt-3 space-y-3 max-h-[640px] overflow-y-auto">
              {mine.map((r) => (
                <div key={r.id} className="border border-[var(--parchment-2)] p-4" data-testid={`my-ratt-card-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">{r.student?.last_name.toUpperCase()} {r.student?.first_name}</span>
                      <span className="text-[var(--muted)]">·</span>
                      <span className="text-[var(--ink-soft)]">{r.class?.name}</span>
                      <span className="text-[var(--muted)]">·</span>
                      <span className="text-[var(--maroon)] font-medium">{r.subject?.name}</span>
                      {r.attendance_status && <StatusBadge status={r.attendance_status} />}
                    </div>
                    {confirmId === r.id ? (
                      <button className="!py-1 !px-2 text-xs bg-[var(--late)] text-white" onClick={() => remove(r.id)} data-testid={`confirm-delete-ratt-${r.id}`}>Confirmer ?</button>
                    ) : (
                      <button className="text-[var(--muted)] hover:text-[var(--late)]" onClick={() => setConfirmId(r.id)} data-testid={`delete-ratt-${r.id}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-[var(--muted)] flex items-center gap-2">
                    <Calendar size={12} /> {r.scheduled_date}
                  </div>
                  <div className="mt-1.5 text-sm text-[var(--ink-soft)] whitespace-pre-line">{r.description}</div>
                  <FilesList files={r.files} photoPath={r.photo_path} />
                </div>
              ))}
              {mine.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucun rattrapage créé pour le moment.</div>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = ATTENDANCE_META[status];
  return (
    <span className="px-2 py-0.5 text-xs font-medium text-white" style={{ background: meta.color }}>
      {meta.label}
    </span>
  );
}
